import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import { useMemo } from "react";
import { generatePath, NavLink } from "react-router-dom";

import Card from "~/components/Card";
import HorizontalStatDetail from "~/components/HorizontalStats/components/HorizontalStatDetail";
import type { HorizontalStat } from "~/components/HorizontalStats/horizontalStatsTypes.ts";
import LoadingSpinnerFull from "~/components/LoadingSpinners/LoadingSpinnerFull";
import OverflowPercentageChart from "~/components/OverflowPercentageChart/OverflowPercentageChart.tsx";
import Tooltip from "~/components/Tooltip";
import { QUOTA_DETAILS_PATH } from "~/constants/appRoutes.ts";
import type { Quota } from "~/modules/client/api/quota/quotaTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	budgetCentsPlanned: number;
	budgetCentsTracked: number;
	manDaysPlanned: number;
	manDaysTracked: number;
	showBudget?: boolean;
	quota?: Quota;
	showLoader?: boolean;
}

const phaseTooltipText = "Das Gesamtbudget der Phase errechnet sich aus der Summe der Budgets aller bestätigten Bestellungen der Phase.\nSind keine bestätigten Bestellungen vorhanden, wird hier der Planwert der Phase angezeigt.";
const quotaTooltipText = "Die Abrechnung dieser Phase erfolgt über ein Abrechnungskontingent, daher werden hier die Budgetwerte des Abrechnungskontingents angezeigt.\n\nDas Budget der Phase dient ausschließlich zu Planungszwecken.\n\nFür Abrechnungskontingente stehen außerdem keine Werte zu Personentagen zur Verfügung.";

const PhaseBudgetStatsSection = ({
	budgetCentsPlanned,
	budgetCentsTracked,
	quota,
	manDaysPlanned,
	manDaysTracked,
	showBudget = true,
	showLoader = false,
}: Props) => {

	const isQuotaStats = !!quota;
	const useCurrencyValues = showBudget || isQuotaStats;
	const plannedValue = useCurrencyValues ? budgetCentsPlanned : manDaysPlanned;
	const trackedValue = useCurrencyValues ? budgetCentsTracked : manDaysTracked;
	const percentageTracked = (trackedValue / plannedValue) * 100;
	const rest = plannedValue - trackedValue;

	const plannedValueFormatted = useCurrencyValues ? formatCentsToCurrency(plannedValue) : plannedValue.toFixed(0) + " PT";
	const trackedValueFormatted = useCurrencyValues ? formatCentsToCurrency(trackedValue) : trackedValue > 0 && trackedValue < 1 ? "<" : "" + +trackedValue.toFixed(0) + " PT";
	const restFormatted = useCurrencyValues ? formatCentsToCurrency(rest) : rest.toFixed(0) + " PT";
	const percentageTrackedFormatted = percentageTracked === Infinity ? "n/a" : percentageTracked.toFixed(0) + "%";

	const stats: HorizontalStat[] = useMemo(() => {
		return [
			{
				label: "Verfügt",
				value: trackedValueFormatted,
			},
			{
				label: "Rest",
				value: restFormatted,
				highlight: rest < 0,
			},
		];
	}, [rest, restFormatted, trackedValueFormatted]);
	const tooltipText = isQuotaStats ? quotaTooltipText : phaseTooltipText;
	const budgetText = isQuotaStats ? "Gesamtbudget" : "Gesamtbudget Phase";
	return <Card>
		{showLoader && <LoadingSpinnerFull />}
		{!showLoader &&
			<div className="flex h-full flex-col justify-center">
				<div className="flex items-center">
					<OverflowPercentageChart percentage={percentageTracked === Infinity ? 0 : percentageTracked}
											 size={90}
											 thickness={0.4}>
						<div className="text-center text-sm font-bold">
							{percentageTrackedFormatted}
						</div>
					</OverflowPercentageChart>

					<div className={clsx("flex h-full grow flex-col justify-start gap-y-2", isQuotaStats ? "justify-start":"justify-center")}>
						{isQuotaStats && <div className="flex justify-center font-bold">
							<NavLink to={generatePath(QUOTA_DETAILS_PATH, {
								clientId: quota?.clientId,
								quotaId: quota.id,
							})}
									 className=" flex items-center gap-x-1 text-primary-400 hover:text-secondary-500"
							>
								Abrechnungskontingent <ArrowTopRightOnSquareIcon className="size-4"/>
							</NavLink>
						</div>}
						<div className="flex items-center justify-between">
							<div className="flex grow flex-col items-center justify-center px-2">
								<div className="flex shrink-0 px-1 text-xs font-medium text-gray-500">
									<span className="mr-1 whitespace-nowrap">{budgetText}</span>
									<Tooltip text={tooltipText}
											 className="whitespace-pre-wrap"
											 width={280}
									>
										<div className="flex size-4 cursor-default select-none items-center justify-center rounded-full bg-gray-300 text-xs text-white">?</div>
									</Tooltip>
								</div>
								<div className="text-lg font-bold text-gray-700">
									{plannedValueFormatted}
								</div>
							</div>
							{stats.map((stat, index) => {
								return <HorizontalStatDetail key={index}
															 label={stat.label}
															 value={stat.value}
															 isHighlighted={stat.isHighlighted} />;
							})}
						</div>
					</div>
				</div>
			</div>}
	</Card>;
};

export default PhaseBudgetStatsSection;